import { apiClient } from "@/api/api-client.ts";
import createBaseService from "@/api/base-service.ts";
import type { ActivityLog, Lead, LeadCategory, LeadDashboard, LeadNote, LeadStatus } from "../../types/lead";

const leadService = {
	...createBaseService<Lead>("/leads"),

	async dashboard(userId?: string): Promise<LeadDashboard> {
		const response = await apiClient.get<LeadDashboard>("/leads/dashboard", {
			params: userId ? { user_id: userId } : {},
		});
		return response.data;
	},

	async activities(leadId: string): Promise<{ data: ActivityLog[]; meta: { current_page: number; last_page: number; total: number } }> {
		const response = await apiClient.get(`/leads/${leadId}/activities`);
		return response.data;
	},

	async categories(): Promise<string[]> {
		const response = await apiClient.get<string[]>("/leads/categories");
		return response.data;
	},

	async generateReferenceNumber(date: string, category: string, clientType: string, leadId?: string): Promise<string> {
		const response = await apiClient.get<{ reference_number: string }>("/leads/generate-reference-number", {
			params: { date, category, client_type: clientType, lead_id: leadId},
		});
		return response.data.reference_number;
	},

	async leadCategories(): Promise<LeadCategory[]> {
		const response = await apiClient.get<{ data: LeadCategory[] }>("/lead-categories");
		return response.data.data;
	},

	async importExcel(file: File, category: string): Promise<{ message: string; imported: number; errors: string[] }> {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("category", category);
		const response = await apiClient.post("/leads/import/excel", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return response.data;
	},

	async importBatch(leads: Record<string, unknown>[]): Promise<{ message: string; imported: number; errors: string[] }> {
		const response = await apiClient.post("/leads/import/batch", { leads });
		return response.data;
	},

	async statuses(): Promise<LeadStatus[]> {
		const response = await apiClient.get<{ data: LeadStatus[] }>("/lead-statuses");
		return response.data.data;
	},

	async createStatus(data: { name: string; color: string; sort_order?: number; is_default?: boolean }): Promise<LeadStatus> {
		const response = await apiClient.post<{ data: LeadStatus }>("/lead-statuses", data);
		return response.data.data;
	},

	async updateStatus(id: string, data: Partial<{ name: string; color: string; sort_order: number; is_default: boolean }>): Promise<LeadStatus> {
		const response = await apiClient.put<{ data: LeadStatus }>(`/lead-statuses/${id}`, data);
		return response.data.data;
	},

	async deleteStatus(id: string): Promise<void> {
		await apiClient.delete(`/lead-statuses/${id}`);
	},

	async notes(leadId: string): Promise<LeadNote[]> {
		const response = await apiClient.get<{ data: LeadNote[] }>(`/leads/${leadId}/notes`);
		return response.data.data;
	},

	async createNote(leadId: string, formData: FormData): Promise<LeadNote> {
		const response = await apiClient.post<{ data: LeadNote }>(`/leads/${leadId}/notes`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return response.data.data;
	},

	async deleteNote(leadId: string, noteId: string): Promise<void> {
		await apiClient.delete(`/leads/${leadId}/notes/${noteId}`);
	},

	noteAttachmentDownloadUrl(noteId: string, mediaId: number): string {
		return `/api/lead-notes/${noteId}/attachments/${mediaId}/download`;
	},
};

export default leadService;
